import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, CircleDollarSignIcon, InfoIcon, LayersIcon, LockKeyholeIcon, Settings2Icon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { GeographySelector } from './GeographySelector';
import { PromotionSettings } from './PromotionSettings';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
import { shushMark, liveMark, maskedMarks } from '../data/recoveryRules';
type Channel = {
  id: string;
  name: string;
  iconUrl?: string;
};
const channels: Channel[] = [{
  id: 'manymoons',
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
}, {
  id: 'prive',
  name: 'Privé',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg'
}, {
  id: 'tiktok',
  name: 'Live Selling',
  iconUrl: liveMark
}, {
  id: 'whatnot',
  name: 'Discreet',
  iconUrl: shushMark
}, {
  id: 'ebay',
  name: 'eBay',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg'
}, {
  id: 'poshmark',
  name: 'Poshmark',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg'
}, {
  id: 'vinted',
  name: 'Vinted',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg'
}, {
  id: 'vestiaire',
  name: 'Vestiaire',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg'
}, {
  id: 'depop',
  name: 'Depop',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg'
}, {
  id: 'mercari',
  name: 'Mercari',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg'
}];
type SubmissionSettings = {
  channels: string[];
  indexPrice: number;
  indexAgreement: number;
  indexLocked: boolean;
};
const defaultSettings: SubmissionSettings = {
  channels: ['manymoons', 'ebay', 'whatnot'],
  indexPrice: 50,
  indexAgreement: 50,
  indexLocked: false
};
const brandSettingOverrides: Record<string, Partial<SubmissionSettings>> = {
  Nisolo: {
    indexPrice: 70,
    channels: ['manymoons', 'whatnot']
  }
};

// Individual uploads can depart from their brand's footprint — damaged goods,
// for example, only move through live selling and discreet channels.
const submissionSettingOverrides: Record<string, Partial<SubmissionSettings>> = {
  '472 — Imperfects': {
    channels: ['manymoons', 'prive', 'tiktok', 'whatnot']
  },
  '473 — Damages': {
    channels: ['tiktok', 'whatnot']
  }
};
export function ControlsDashboard({
  selectedOwner = '',
  initialScopeId = null,
  isAdmin = false
}: {
  selectedOwner?: string;
  initialScopeId?: string | null;
  isAdmin?: boolean;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const {
    data: nisoloSubmissions
  } = useNisoloSubmissions(includes('Nisolo'));
  const submissions = useMemo(() => {
    const rows: {
      id: string;
      brand: string;
      name: string;
      arrived: string;
      units: number;
    }[] = [];
    const push = (brand: string, batches: {
      arrived: string;
      name: string;
      received: number;
    }[]) => {
      batches.forEach((batch, index) => rows.push({
        id: submissionId(brand, index, batch.name),
        brand,
        name: batch.name,
        arrived: batch.arrived,
        units: batch.received
      }));
    };
    if (includes('1 People')) push('1 People', onePeopleIncomingBatches);
    if (includes('Eddy')) push('Eddy', eddyIncomingBatches);
    if (includes('Nisolo')) push('Nisolo', nisoloSubmissions?.submissions ?? []);
    return rows;
  }, [selectedOwner, nisoloSubmissions]);
  const [scopeId, setScopeId] = useState<string>(initialScopeId ?? '');
  const [scopeOpen, setScopeOpen] = useState(false);
  // Arriving from an upload's Guardrails button lands directly on that submission.
  // Submissions load asynchronously, so the request is held until it resolves.
  const pendingScopeId = useRef<string | null>(initialScopeId);
  useEffect(() => {
    if (initialScopeId) {
      pendingScopeId.current = initialScopeId;
      setScopeId(initialScopeId);
    }
  }, [initialScopeId]);
  useEffect(() => {
    const requested = pendingScopeId.current;
    if (requested) {
      if (submissions.some(submission => submission.id === requested)) {
        pendingScopeId.current = null;
        setScopeId(requested);
      }
      return;
    }
    // Fall back to the first submission in view whenever the current one disappears.
    setScopeId(current => submissions.some(submission => submission.id === current) ? current : submissions[0]?.id ?? '');
  }, [submissions]);
  const activeSubmission = submissions.find(submission => submission.id === scopeId) ?? null;

  // Every submission keeps its own rules; switching the selector swaps them out.
  const [rulesById, setRulesById] = useState<Record<string, SubmissionSettings>>({});
  // Some brands carry their own negotiated pricing limit as a starting point.
  const brandDefaults = (brand?: string, batchName?: string): SubmissionSettings => {
    const merged = {
      ...defaultSettings,
      ...(brand ? brandSettingOverrides[brand] ?? {} : {}),
      ...(batchName ? submissionSettingOverrides[batchName] ?? {} : {})
    };
    return {
      ...merged,
      indexAgreement: merged.indexPrice
    };
  };
  const scopeDefaults = brandDefaults(activeSubmission?.brand, activeSubmission?.name);
  const settings = rulesById[scopeId] ?? scopeDefaults;
  const updateSettings = (patch: Partial<SubmissionSettings>) => {
    if (!scopeId) return;
    setRulesById(current => ({
      ...current,
      [scopeId]: {
        ...(current[scopeId] ?? scopeDefaults),
        ...patch
      }
    }));
  };
  const {
    channels: activeChannels,
    indexPrice,
    indexAgreement,
    indexLocked
  } = settings;
  const [infoChannel, setInfoChannel] = useState<Channel | null>(null);
  const toggleChannel = (channelId: string) => {
    updateSettings({
      channels: activeChannels.includes(channelId) ? activeChannels.filter(id => id !== channelId) : [...activeChannels, channelId]
    });
  };

  // Locking pins the index price to the agreed percentage; the slider keeps its
  // full 0-100 range so the setting stays readable in context.
  const updateIndexAgreement = (value: number) => {
    const nextValue = Math.max(0, Math.min(100, value));
    updateSettings({
      indexAgreement: nextValue,
      indexPrice: indexLocked ? nextValue : indexPrice
    });
  };
  const setIndexAgreementLock = (locked: boolean) => {
    updateSettings({
      indexLocked: locked,
      indexPrice: locked ? indexAgreement : indexPrice
    });
  };
  const setIndexPrice = (value: number) => updateSettings({
    indexPrice: value
  });
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="controls-heading" data-id="element-599" data-mp="1t7f5p29o5fb1">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-600" data-mp="15hks2ihizmpq">
        <div data-id="element-601" data-mp="10jjak1zqzvgd">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-602" data-mp="10hpy262geh0g">
            <Settings2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-603" data-mp="3c4tj1oie1nvf" />
            Controls
          </span>
          <h1 id="controls-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-604" data-mp="3gyxr6b3e41y3">
            Set your recovery rules.
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-605" data-mp="385azaw2aziu6">
            Choose which submissions these rules cover, where that inventory can be sold, and the price guardrails Carli should use.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-606" data-mp="3lvwkwsj566vr">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-607" data-mp="3qu7r8ta89z98" />
          Changes save automatically
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-5 sm:p-6" aria-labelledby="scope-heading" data-id="element-608" data-mp="2afg00jotiy1i">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-609" data-mp="373f3jahzsypl">
          <div data-id="element-610" data-mp="324bzntdr0yj7">
            <div className="flex items-center gap-2" data-id="element-611" data-mp="323sttb1di0lj">
              <LayersIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-612" data-mp="3230x1ww1hwto" />
              <h2 id="scope-heading" className="text-lg font-semibold" data-id="element-613" data-mp="2x5rb4r7v03al">Applies to</h2>
            </div>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-ink/60" data-id="element-614" data-mp="2s7phxdgpynle">
              {activeSubmission ? `Rules below apply to ${activeSubmission.name} only — ${activeSubmission.units.toLocaleString('en-US')} units uploaded ${activeSubmission.arrived}. Each submission keeps its own rules.` : 'Upload inventory to set rules for a specific submission.'}
            </p>
          </div>

          <div className="relative w-full lg:w-80" data-id="element-615" data-mp="3b2xog69td9sf">
            <button type="button" onClick={() => setScopeOpen(open => !open)} aria-expanded={scopeOpen} aria-haspopup="listbox" disabled={submissions.length === 0} className="flex w-full items-center justify-between gap-3 rounded-xl border border-ink/15 bg-paper px-4 py-3 text-left text-sm font-semibold transition-colors hover:border-[#BB5E40]/45 disabled:opacity-50" data-id="element-616" data-mp="cg1wae75ohhw">
              <span className="truncate" data-id="element-617" data-mp="twfllpiwzffb">{activeSubmission?.name ?? 'No submissions yet'}</span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${scopeOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-618" data-mp="yw1w3r7cyhkf" />
            </button>

            {scopeOpen && <div role="listbox" aria-label="Inventory submissions" className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg lg:w-96" data-id="element-619" data-mp="17odmjd2h250e">
                <span className="block px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-620" data-mp="1cp1afpvem5qe">Choose one submission</span>
                <div className="max-h-72 overflow-y-auto" data-id="element-621" data-mp="1mkl7iflpfagq">
                  {submissions.map(submission => {
                const selected = submission.id === scopeId;
                const customized = Boolean(rulesById[submission.id]);
                return <button key={submission.id} type="button" role="option" aria-selected={selected} onClick={() => {
                  setScopeId(submission.id);
                  setScopeOpen(false);
                }} className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${selected ? 'bg-[#BB5E40]/10' : 'hover:bg-cream/70'}`} data-id="element-622" data-mp="dpfo0jrgj3r0">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center" data-id="element-623" data-mp="11cnvn8ccyow7">
                          {selected && <CheckIcon className="h-3.5 w-3.5 text-[#BB5E40]" aria-hidden="true" data-id="element-624" data-mp="2y9emfmgv0q4h" />}
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-625" data-mp="2vmcbz22c12nv">
                          <span className="block truncate text-sm font-medium" data-id="element-626" data-mp="2vnx6wssk6nm9">{submission.name}</span>
                          <span className="block text-xs text-ink/50" data-id="element-627" data-mp="2qtsuvv4frpeb">
                            {allBrands ? `${submission.brand} · ` : ''}{submission.arrived} · {submission.units.toLocaleString('en-US')} units
                          </span>
                        </span>
                        {customized && <span className="shrink-0 rounded-full bg-[#BB5E40]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-628" data-mp="3ekosx9zhjgu0">Custom</span>}
                      </button>;
              })}
                </div>
              </div>}
          </div>
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="channels-heading" data-id="element-629" data-mp="1t7vlcn81auei">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-630" data-mp="1p5h3o6g3q0ot">
          <div data-id="element-631" data-mp="1qoqragop9ke3">
            <div className="flex items-center gap-2" data-id="element-632" data-mp="1qo8muwrvvzv3">
              <SlidersHorizontalIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-633" data-mp="1vmii17dlz0gp" />
              <h2 id="channels-heading" className="text-lg font-semibold" data-id="element-634" data-mp="1gprsdptql34a">Approved channels</h2>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-635" data-mp="1lo30nw4dv5ke">Select the marketplaces where this inventory is eligible to appear.</p>
          </div>
          <span className="text-sm font-medium text-ink/55" data-id="element-636" data-mp="17v4pp1fgj4vq">{activeChannels.length} selected</span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-637" data-mp="1cpr4lekqzfkk">
          {channels.map(channel => {
          const selected = activeChannels.includes(channel.id);
          const infoOpen = infoChannel?.id === channel.id;
          return <div key={channel.id} className="relative" data-id="element-638" data-mp="ozmyn0k373qe">
                <button type="button" onClick={() => toggleChannel(channel.id)} aria-pressed={selected} className={`flex min-h-[76px] w-full items-center gap-2.5 rounded-xl border p-3 pr-7 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'border-[#BB5E40] bg-[#BB5E40] text-cream shadow-sm' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/30'}`} data-id="element-639" data-mp="szp31hh7ib9d">
                  <ChannelBadge channel={channel} selected={selected} data-id="element-640" data-mp="t9h5xhd622de" />
                  <span className={`min-w-0 flex-1 whitespace-normal break-words text-[13px] font-semibold leading-tight sm:text-sm ${selected ? 'text-cream' : 'text-ink'}`} data-id="element-641" data-mp="obxqwf8n9lke">{channel.name}</span>
                </button>
                <button type="button" onClick={() => setInfoChannel(infoOpen ? null : channel)} aria-label={`About ${channel.name}`} aria-expanded={infoOpen} className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'text-cream/75 hover:bg-cream/15 hover:text-cream' : 'text-ink/45 hover:bg-ink/5 hover:text-[#BB5E40]'}`} data-id="element-642" data-mp="y2a8pzp2r0mu">
                  <InfoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-643" data-mp="24voiya6e6dna" />
                </button>
                {infoOpen && <div role="dialog" aria-label={`About ${channel.name}`} className="absolute left-0 top-[calc(100%+8px)] z-20 w-64 rounded-xl border border-ink/10 bg-paper p-4 text-left shadow-lg" data-id="element-644" data-mp="186qnsquqcdpl">
                    <div className="flex items-start justify-between gap-3" data-id="element-645" data-mp="1h49d47fepe7a">
                      <div data-id="element-646" data-mp="1m5ypbqed7xwy">
                        <p className="text-sm font-semibold text-ink" data-id="element-647" data-mp="1m68hnkmvuh5t">{channel.name}</p>
                        <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-648" data-mp="2skhishq4x5n6">Channel description coming soon. Add the approved positioning, audience, and merchandising notes for this marketplace here.</p>
                      </div>
                      <button type="button" onClick={() => setInfoChannel(null)} className="-mr-1 -mt-1 rounded-md p-1 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink" aria-label={`Close ${channel.name} details`} data-id="element-649" data-mp="lyl752rzr60z">
                        <XIcon className="h-3.5 w-3.5" data-id="element-650" data-mp="37ajcuhkb9db" />
                      </button>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </section>

      <section className="mt-5" data-id="element-651" data-mp="3pdzb2s9v7e14">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="pricing-heading" data-id="element-652" data-mp="3kee2rr8t1tbq">
          <div className="flex items-center gap-2" data-id="element-653" data-mp="1nmma2eb0pqac">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-654" data-mp="1nlb5wddnugip" />
            <h2 id="pricing-heading" className="text-lg font-semibold" data-id="element-655" data-mp="1inj32obh99zq">Pricing guardrails</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-656" data-mp="3d1x02k16sn47">This setting guides how your inventory is publicly merchandised.</p>

          <div className="mt-7 space-y-7" data-id="element-657" data-mp="335k6dm5n699g">
            <PricingSlider label="Public pricing limit" value={indexPrice} onChange={setIndexPrice} helper="The maximum discount from MSRP at which inventory can be publicly listed." min={0} max={100} agreement={indexAgreement} onAgreementChange={updateIndexAgreement} locked={indexLocked} canUnlock={isAdmin} onLockChange={setIndexAgreementLock} data-id="element-658" data-mp="336my1wasgfqf" />
          </div>
        </article>
      </section>

      <GeographySelector brand={activeSubmission?.brand} data-id="element-659" data-mp="2bdmhoeuqre3e" />

      <PromotionSettings brand={activeSubmission?.brand} submissionName={activeSubmission?.name} data-id="element-660" data-mp="2bg8pmfljnvm5" />
    </div>;
}
function ChannelBadge({
  channel,
  selected
}: {
  channel: Channel;
  selected: boolean;
}) {
  // The Discreet mark is a solid glyph, so it is masked to render off-white
  // like the other channel logos rather than showing as black artwork.
  const masked = !!channel.iconUrl && maskedMarks.includes(channel.iconUrl);
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 ${selected ? 'bg-cream/15' : 'bg-[#BB5E40]'}`} data-id="element-661" data-mp="2cururoaxr0jf">
      {masked ? <span aria-hidden="true"
    // The Live mark is a wide lockup, so it is scaled up to sit at the
    // same optical weight as the Manymoons M beside it.
    className={`h-full w-full bg-cream ${channel.iconUrl === liveMark ? 'scale-[1.45]' : ''}`} style={{
      WebkitMaskImage: `url(${channel.iconUrl})`,
      maskImage: `url(${channel.iconUrl})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-662" data-mp="1p34bnsjx0trp" /> : <img src={channel.iconUrl} alt="" className={`h-full w-full object-contain ${channel.id === 'ebay' ? 'scale-125' : ''}`} data-id="element-663" data-mp="kw66b134przs" />}
    </span>;
}
function PricingSlider({
  label,
  value,
  onChange,
  helper,
  min,
  max,
  agreement,
  onAgreementChange,
  locked,
  canUnlock,
  onLockChange
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  helper: string;
  min: number;
  max: number;
  agreement: number;
  onAgreementChange: (value: number) => void;
  locked: boolean;
  canUnlock: boolean;
  onLockChange: (locked: boolean) => void;
}) {
  const agreementId = `${label.toLowerCase().replace(' ', '-')}-agreement`;
  const lockId = `${label.toLowerCase().replace(' ', '-')}-lock`;
  return <div data-id="element-664" data-mp="1hk2h1pbvxt33">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-665" data-mp="1ml00044pyr5n">
        <label className="pt-1 text-sm font-semibold text-ink" data-id="element-666" data-mp="373106t3l7ab5">{label}</label>
        <div className="flex items-center gap-2 self-start rounded-xl border border-ink/10 bg-cream/65 p-1.5" data-id="element-667" data-mp="374c0uk1qk1y1">
          <label htmlFor={agreementId} className="sr-only" data-id="element-668" data-mp="326am2bbceosd">Agreed {label.toLowerCase()} discount off MSRP</label>
          <input id={agreementId} type="number" min={min} max={100} value={agreement} disabled={locked} onChange={event => onAgreementChange(Number(event.target.value))} className={`w-12 bg-transparent text-right text-sm font-semibold tabular-nums text-ink focus:outline-none ${locked ? 'cursor-not-allowed text-ink/50' : ''}`} data-id="element-669" data-mp="2t39h4dy7r3qa" />
          <span className="text-xs font-medium text-ink/50" data-id="element-670" data-mp="3c1vh1phzuosm">% off agreed</span>
          <span className="h-5 w-px bg-ink/10" aria-hidden="true" data-id="element-671" data-mp="dcx0fhtu0bfh" />
          {canUnlock ? <>
              <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-4 w-4 rounded border-ink/25 accent-[#BB5E40]" data-id="element-672" data-mp="8ju5b59eotx6" />
              <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-673" data-mp="gbinayhgsxi8">
                <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-674" data-mp="bd7fsuen6z36" />
                Lock
              </label>
            </> : <span className="flex items-center gap-1.5 text-xs font-semibold text-ink/55" data-id="element-675" data-mp="140ttwwlhxm4a">
              <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-676" data-mp="z7qzkm8wa8n1" />
              {locked ? 'Locked' : 'Unlocked'}
            </span>}
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink/55" data-id="element-677" data-mp="1rrpep9rjbh21">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-678" data-mp="1mwtcxhwhxfbq" />
        <p data-id="element-679" data-mp="1v9d4yzwwy58g">{helper}</p>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3" data-id="element-680" data-mp="1z8vrg04duode">
        <span className="text-xs font-medium uppercase tracking-wider text-ink/45" data-id="element-681" data-mp="1zb8726mo4mna">Current setting</span>
        <span className="font-display text-2xl font-semibold tabular-nums text-[#BB5E40]" data-id="element-682" data-mp="24abajmn02kt5">{value}% <span className="font-sans text-xs font-medium text-ink/45" data-id="element-683" data-mp="2958jktka4u61">OFF MSRP</span></span>
      </div>
      <input type="range" min={min} max={max} step={1} value={value} disabled={locked} onChange={event => onChange(Number(event.target.value))} className={`mt-3 w-full accent-[#BB5E40] ${locked ? 'cursor-not-allowed opacity-60' : ''}`} aria-label={`${label} percentage off MSRP`} data-id="element-684" data-mp="2e4bqyknqfch6" />
      <div className="mt-1 flex justify-between text-xs text-ink/45" data-id="element-685" data-mp="2wxysetwvyxc3">
        <span data-id="element-686" data-mp="31vquuhv5q1uj">{min}%</span>
        <span data-id="element-687" data-mp="31x1y8gkox7l4">{max}%</span>
      </div>
      {locked && <p className="mt-2 text-xs font-medium text-[#94452E]" data-id="element-688" data-mp="2c7uxilrtnobw">
          Locked at the agreed {agreement}% off MSRP. {canUnlock ? 'Unlock to adjust.' : 'Contact ManyCo to change your agreement.'}
        </p>}
    </div>;
}